import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import type { Workspace } from '@/data/mock'

const ACTIVE_WORKSPACE_KEY = 'pharmatext_active_workspace_id'

function readSavedWorkspaceId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY)
  } catch {
    return null
  }
}

function writeSavedWorkspaceId(id: string | null) {
  try {
    if (id) localStorage.setItem(ACTIVE_WORKSPACE_KEY, id)
    else localStorage.removeItem(ACTIVE_WORKSPACE_KEY)
  } catch {
    // private browsing — silently degrade
  }
}

interface WorkspaceContextValue {
  workspaces: Workspace[]
  activeWorkspaceId: string | null
  setActiveWorkspaceId: (id: string | null) => void
  createWorkspace: (name: string) => Promise<void>
  renameWorkspace: (id: string, name: string) => Promise<void>
  deleteWorkspace: (id: string) => Promise<void>
  isLoading: boolean
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [activeWorkspaceId, _setActiveWorkspaceId] = useState<string | null>(readSavedWorkspaceId)
  const [isLoading, setIsLoading] = useState(true)

  const setActiveWorkspaceId = useCallback((id: string | null) => {
    _setActiveWorkspaceId(id)
    writeSavedWorkspaceId(id)
  }, [])

  useEffect(() => {
    const savedId = readSavedWorkspaceId()
    apiFetch('/api/workspaces')
      .then((r) => { if (!r.ok) return; return r.json() })
      .then((data: Workspace[] | undefined) => {
        if (!Array.isArray(data)) return
        setWorkspaces(data)

        if (data.length === 0) {
          setActiveWorkspaceId(null)
        } else if (data.length === 1) {
          setActiveWorkspaceId(data[0].id)
        } else if (savedId && data.some((w) => w.id === savedId)) {
          setActiveWorkspaceId(savedId)
        } else {
          setActiveWorkspaceId(null)
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [setActiveWorkspaceId])

  const createWorkspace = useCallback(async (name: string) => {
    const r = await apiFetch('/api/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
    if (!r.ok) throw new Error(`Failed to create workspace: ${r.status}`)
    const created: Workspace = await r.json()
    setWorkspaces((prev) => [...prev, created])
    setActiveWorkspaceId(created.id)
  }, [setActiveWorkspaceId])

  const renameWorkspace = useCallback(async (id: string, name: string) => {
    const r = await apiFetch(`/api/workspaces/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    })
    if (!r.ok) throw new Error(`Failed to rename workspace: ${r.status}`)
    const updated: Workspace = await r.json()
    setWorkspaces((prev) => prev.map((w) => (w.id === id ? updated : w)))
  }, [])

  const deleteWorkspace = useCallback(async (id: string) => {
    const r = await apiFetch(`/api/workspaces/${id}`, { method: 'DELETE' })
    if (!r.ok) {
      if (r.status === 409) throw new Error('WORKSPACE_HAS_SOURCES')
      throw new Error(`Failed to delete workspace: ${r.status}`)
    }
    setWorkspaces((prev) => prev.filter((w) => w.id !== id))
    _setActiveWorkspaceId((prev) => {
      if (prev === id) {
        writeSavedWorkspaceId(null)
        return null
      }
      return prev
    })
  }, [])

  return (
    <WorkspaceContext.Provider value={{ workspaces, activeWorkspaceId, setActiveWorkspaceId, createWorkspace, renameWorkspace, deleteWorkspace, isLoading }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook colocated with provider
export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider')
  return ctx
}
